import { useEffect, useMemo, useState } from "react";
import { FiX } from "react-icons/fi";
import {
  addUserToBranch,
  createUser,
  getBranchUsers,
  getCashRegisters,
  removeUserFromBranch,
  setCashRegisterUsers,
  updateUser,
  updateUserRoles,
} from "../api";
import { normalizePaginatedResponse } from "../utils/pagination";
import InlineLoading from "./InlineLoading";

const UserModal = ({
  user,
  branches = [],
  roles = [],
  canManagePermissions,
  canManageRegisters,
  canUpdateUser,
  canCreateUser,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(user?.id);
  const resolvedCanManagePermissions = Boolean(canManagePermissions);
  const resolvedCanManageRegisters = Boolean(canManageRegisters);
  const resolvedCanUpdateUser = Boolean(canUpdateUser);
  const resolvedCanCreateUser = Boolean(canCreateUser);
  const [form, setForm] = useState({
    name: "",
    username: "",
    password: "",
  });
  const [branchAccess, setBranchAccess] = useState({});
  const [branchRoles, setBranchRoles] = useState({});
  const [globalRoleId, setGlobalRoleId] = useState("");
  const [branchRegisters, setBranchRegisters] = useState({});
  const [initialBranchAccess, setInitialBranchAccess] = useState({});
  const [initialRegisterAccess, setInitialRegisterAccess] = useState({});
  const [registersByBranch, setRegistersByBranch] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const adminRole = useMemo(
    () => roles.find((role) => role.name === "Admin"),
    [roles]
  );
  const branchRoleOptions = useMemo(
    () => roles.filter((role) => role.name !== "Admin"),
    [roles]
  );

  useEffect(() => {
    if (!user) {
      setForm({ name: "", username: "", password: "" });
      setBranchAccess({});
      setBranchRoles({});
      setGlobalRoleId("");
      setBranchRegisters({});
      setInitialBranchAccess({});
      setInitialRegisterAccess({});
      setRegistersByBranch({});
      setError("");
      return;
    }
    setForm({
      name: user.name || "",
      username: user.username || "",
      password: "",
    });

    const roleMap = {};
    let globalRole = "";
    (user.Roles || []).forEach((role) => {
      const branchId = role?.UserRole?.branchId;
      if (branchId === null || branchId === undefined) {
        if (role.name === "Admin") {
          globalRole = String(role.id);
        }
      } else {
        roleMap[String(branchId)] = String(role.id);
      }
    });
    setBranchRoles(roleMap);
    setGlobalRoleId(globalRole);
  }, [user]);

  useEffect(() => {
    let isMounted = true;
    const loadAccessData = async () => {
      if (!isEdit || !branches.length) return;
      setLoading(true);
      setError("");
      try {
        const branchUsers = await Promise.all(
          branches.map((branch) =>
            getBranchUsers(branch.id, { pageSize: 0 })
              .then((data) => ({
                branchId: branch.id,
                data: normalizePaginatedResponse(data).data || [],
              }))
              .catch(() => ({ branchId: branch.id, data: [] }))
          )
        );
        const accessMap = {};
        branchUsers.forEach(({ branchId, data }) => {
          accessMap[String(branchId)] = data.some(
            (entry) => String(entry.userId) === String(user.id)
          );
        });

        if (!isMounted) return;
        setBranchAccess((prev) => {
          const next = { ...accessMap };
          Object.keys(prev).forEach((key) => {
            if (prev[key]) next[key] = true;
          });
          Object.keys(branchRoles).forEach((key) => {
            next[key] = true;
          });
          return next;
        });
        setInitialBranchAccess(accessMap);

        if (resolvedCanManageRegisters) {
          const registers = await Promise.all(
            branches.map((branch) =>
              getCashRegisters(branch.id, { pageSize: 0 })
                .then((data) => ({
                  branchId: branch.id,
                  data: normalizePaginatedResponse(data).data || [],
                }))
                .catch(() => ({ branchId: branch.id, data: [] }))
            )
          );
          const registerMap = {};
          const selectedMap = {};
          registers.forEach(({ branchId, data }) => {
            registerMap[String(branchId)] = data || [];
            const selected = new Set();
            (data || []).forEach((register) => {
              const users = register.CashRegisterUsers || [];
              const isSelected = users.some(
                (entry) =>
                  entry?.isActive !== false
                  && String(entry.userId) === String(user.id)
              );
              if (isSelected) {
                selected.add(String(register.id));
              }
            });
            selectedMap[String(branchId)] = selected;
          });
          if (!isMounted) return;
          setRegistersByBranch(registerMap);
          setBranchRegisters(selectedMap);
          setInitialRegisterAccess(selectedMap);
        }
      } catch (err) {
        if (!isMounted) return;
        setError("No se pudo cargar la configuracion de acceso.");
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadAccessData();
    return () => {
      isMounted = false;
    };
  }, [branches, resolvedCanManageRegisters, isEdit, user, branchRoles]);

  const toggleBranch = (branchId) => {
    if (!resolvedCanUpdateUser) return;
    const key = String(branchId);
    setBranchAccess((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (next[key] && !branchRoles[key] && branchRoleOptions.length) {
        setBranchRoles((rolesPrev) => ({
          ...rolesPrev,
          [key]: String(branchRoleOptions[0].id),
        }));
      }
      if (!next[key]) {
        setBranchRoles((rolesPrev) => {
          const updated = { ...rolesPrev };
          delete updated[key];
          return updated;
        });
        setBranchRegisters((prevRegisters) => ({
          ...prevRegisters,
          [key]: new Set(),
        }));
      }
      return next;
    });
  };

  const updateBranchRole = (branchId, roleId) => {
    const key = String(branchId);
    setBranchRoles((prev) => ({ ...prev, [key]: roleId }));
  };

  const toggleRegister = (branchId, registerId) => {
    const branchKey = String(branchId);
    const registerKey = String(registerId);
    setBranchRegisters((prev) => {
      const next = new Map();
      Object.entries(prev).forEach(([key, setValue]) => {
        next.set(key, new Set(setValue));
      });
      const selected = next.get(branchKey) || new Set();
      if (selected.has(registerKey)) {
        selected.delete(registerKey);
      } else {
        selected.add(registerKey);
      }
      next.set(branchKey, selected);
      const normalized = {};
      next.forEach((value, key) => {
        normalized[key] = value;
      });
      return normalized;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    let resolvedUserId = user?.id;
    try {
      if (!isEdit && !resolvedCanCreateUser) {
        setError("No tienes permisos para crear usuarios.");
        return;
      }
      const payload = {
        name: form.name,
        username: form.username,
      };
      if (!isEdit && form.password) {
        payload.password = form.password;
      }
      if (isEdit) {
        if (resolvedCanUpdateUser) {
          await updateUser(resolvedUserId, payload);
        }
      } else {
        const created = await createUser(payload);
        resolvedUserId = created.id;
      }

      if (resolvedCanUpdateUser) {
        const branchTasks = [];
        branches.forEach((branch) => {
          const key = String(branch.id);
          const wantsAccess = Boolean(branchAccess[key]);
          const hadAccess = Boolean(initialBranchAccess[key]);
          if (wantsAccess && !hadAccess) {
            branchTasks.push(addUserToBranch({ branchId: branch.id, userId: resolvedUserId }));
          }
          if (!wantsAccess && hadAccess) {
            branchTasks.push(removeUserFromBranch({ branchId: branch.id, userId: resolvedUserId }));
          }
        });
        if (branchTasks.length) {
          await Promise.all(branchTasks);
        }
      }

      if (resolvedCanManagePermissions) {
        const roleAssignments = [];
        if (globalRoleId) {
          roleAssignments.push({ roleId: Number(globalRoleId), branchId: null });
        }
        branches.forEach((branch) => {
          const key = String(branch.id);
          if (!branchAccess[key]) return;
          const roleId = branchRoles[key];
          if (!roleId) return;
          roleAssignments.push({
            roleId: Number(roleId),
            branchId: branch.id,
          });
        });
        await updateUserRoles(resolvedUserId, roleAssignments);
      }

      if (resolvedCanManageRegisters) {
        const registerUpdates = [];
        branches.forEach((branch) => {
          const branchKey = String(branch.id);
          const registers = registersByBranch[branchKey] || [];
          const selected = branchRegisters[branchKey] || new Set();
          const initialSelected = initialRegisterAccess[branchKey] || new Set();
          registers.forEach((register) => {
            const registerKey = String(register.id);
            const changed = selected.has(registerKey) !== initialSelected.has(registerKey);
            if (!changed) return;
            const existing = (register.CashRegisterUsers || [])
              .filter((entry) => entry?.isActive !== false)
              .filter((entry) => String(entry.userId) !== String(resolvedUserId))
              .map((entry) => ({
                userId: entry.userId,
                role: entry.role || null,
                isActive: entry.isActive !== false,
              }));
            if (selected.has(registerKey)) {
              existing.push({
                userId: resolvedUserId,
                role: null,
                isActive: true,
              });
            }
            registerUpdates.push(
              setCashRegisterUsers(branch.id, register.id, existing)
            );
          });
        });
        if (registerUpdates.length) {
          await Promise.all(registerUpdates);
        }
      }

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.message || "No se pudo guardar el usuario.");
    } finally {
      setSaving(false);
    }
  };

  const renderBranchRoles = () => {
    if (!branches.length) {
      return <p className="text-sm text-gray-500">No hay sucursales disponibles.</p>;
    }
    return (
      <div className="space-y-3">
        {branches.map((branch) => {
          const key = String(branch.id);
          const hasAccess = Boolean(branchAccess[key]);
          const registers = registersByBranch[key] || [];
          const selectedRegisters = branchRegisters[key] || new Set();
          return (
            <div key={branch.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={hasAccess}
                    onChange={() => toggleBranch(branch.id)}
                    disabled={!resolvedCanUpdateUser}
                  />
                  {branch.branchName}
                </label>
                <select
                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm"
                  value={branchRoles[key] || ""}
                  onChange={(e) => updateBranchRole(branch.id, e.target.value)}
                  disabled={!hasAccess || !resolvedCanManagePermissions}
                >
                  <option value="">Sin rol</option>
                  {branchRoleOptions.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>

              {resolvedCanManageRegisters && hasAccess && registers.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs uppercase text-gray-500 mb-2">
                    Cajas permitidas
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {registers.map((register) => (
                      <label key={register.id} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={selectedRegisters.has(String(register.id))}
                          onChange={() => toggleRegister(branch.id, register.id)}
                        />
                        {register.name || `Caja ${register.id}`}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex justify-center items-start z-50 px-4 py-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Usuarios</p>
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? "Editar usuario" : "Crear usuario"}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Define acceso por sucursal, roles y cajas disponibles.
            </p>
          </div>
          <button
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
            onClick={onClose}
          >
            <FiX size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                disabled={isEdit && !resolvedCanUpdateUser}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Email</label>
              <input
                type="email"
                value={form.username}
                onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                required
                disabled={isEdit}
              />
            </div>
            {!isEdit && (
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Contraseña123 (por defecto)"
                  disabled={!resolvedCanCreateUser}
                />
              </div>
            )}
          </div>

          {adminRole && resolvedCanManagePermissions && (
            <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={globalRoleId === String(adminRole.id)}
                  onChange={(e) =>
                    setGlobalRoleId(e.target.checked ? String(adminRole.id) : "")
                  }
                />
                Rol global: Admin (acceso total a sucursales)
              </label>
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold mb-2">Acceso por sucursal</h3>
            {loading ? (
              <p className="text-sm text-gray-500">
                <InlineLoading label="Cargando accesos" />
              </p>
            ) : (
              renderBranchRoles()
            )}
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div className="text-xs text-gray-500">
              Asegúrate de asignar rol y cajas si corresponde.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={saving}
              >
                {saving ? <InlineLoading label="Guardando" /> : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserModal;
